<template>
  <el-date-picker
    v-model="internalValue"
    type="datetimerange"
    :picker-options="resolvedPickerOptions"
    :start-placeholder="placeholder[0]"
    :end-placeholder="placeholder[1]"
    range-separator="ー"
    prefix-icon="mdi mdi-calendar-clock"
    suffix-icon=""
    :size="size"
    :disabled="disabled"
    format="yyyy-MM-dd HH:mm"
    value-format="yyyy-MM-dd HH:mm"
    @change="handleChange"
    :clearable="false"
    class="app-date-range-picker"
  />
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
export default {
  name: 'AppDateRangePicker',

  props: {
    value: {
      type: Array,
      default: () => ['', '']
    },
    campaign: {
      type: Object,
      default: null
    },
    campaignPrize: {
      type: Object,
      default: null
    },
    onChange: {
      type: Function,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'small',
      validator: value => ['medium', 'small', 'mini'].includes(value)
    },
    placeholder: {
      type: Array,
      default: () => ['開始日時', '終了日時']
    },
    clearable: {
      type: Boolean,
      default: false
    },
    pickerOptions: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      internalValue: this.value || ['', '']
    }
  },

  computed: {
    placeholderText() {
      return this.placeholder.join('〜')
    },

    resolvedPickerOptions() {
      return this.pickerOptions || this.defaultPickerOptions
    },

    defaultPickerOptions() {
      const startDate = this.getStartDate()
      const endDate = this.getEndDate()

      return {
        disabledDate(date) {
          const dateTime = new Date(date)
          const startDateTime = new Date(startDate)
          const endDateTime = new Date(endDate)

          const dateOnly = new Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate())
          const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate())
          const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate())

          return dateOnly < startDateOnly || dateOnly > endDateOnly
        }
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (this.campaign && !this.value[0] && !this.value[1]) {
        if (this.campaign.type === 'old_web_instantwin') {
          if (
            (this.campaignPrize && (this.campaignPrize.startDatetime || this.campaignPrize.endDatetime)) ||
            this.campaign.startDatetime ||
            this.campaign.endDatetime
          ) {
            this.setDefaultDateRange()
          }
        } else {
          this.setDefaultDateRange()
        }
      }
    })
  },

  watch: {
    value: {
      handler(newVal) {
        this.internalValue = newVal || ['', '']
      },
      immediate: true
    },
    campaign: {
      handler(newVal) {
        if (newVal && !this.value[0] && !this.value[1]) {
          this.$nextTick(() => {
            if (newVal.type === 'old_web_instantwin') {
              if (
                (this.campaignPrize && (this.campaignPrize.startDatetime || this.campaignPrize.endDatetime)) ||
                newVal.startDatetime ||
                newVal.endDatetime
              ) {
                this.setDefaultDateRange()
              }
            } else {
              this.setDefaultDateRange()
            }
          })
        }
      },
      immediate: false
    },
    campaignPrize: {
      handler(newVal) {
        if (
          newVal &&
          this.campaign &&
          this.campaign.type === 'old_web_instantwin' &&
          !this.value[0] &&
          !this.value[1]
        ) {
          this.$nextTick(() => {
            if (
              newVal.startDatetime ||
              newVal.endDatetime ||
              this.campaign.startDatetime ||
              this.campaign.endDatetime
            ) {
              this.setDefaultDateRange()
            }
          })
        }
      },
      immediate: false
    }
  },

  methods: {
    /**
     * 開始日時を取得（Web IW対応）
     */
    getStartDate() {
      if (this.campaign?.type === 'old_web_instantwin' && this.campaignPrize?.startDatetime) {
        return this.campaignPrize.startDatetime
      }
      return this.campaign?.startDatetime || new Date()
    },

    /**
     * 終了日時を取得（Web IW対応）
     */
    getEndDate() {
      if (this.campaign?.type === 'old_web_instantwin' && this.campaignPrize?.endDatetime) {
        return this.campaignPrize.endDatetime
      }
      return this.campaign?.endDatetime || new Date()
    },

    /**
     * 日時フォーマット
     */
    formatDatetime(datetime) {
      if (!datetime) return ''
      const date = new Date(datetime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}`
    },

    /**
     * デフォルト期間を設定
     */
    setDefaultDateRange() {
      const startDate = this.getStartDate()
      const endDate = this.getEndDate()

      if (startDate && endDate) {
        const formattedRange = [this.formatDatetime(startDate), this.formatDatetime(endDate)]
        this.internalValue = formattedRange
        this.handleChange(formattedRange)
      }
    },

    /**
     * 日付範囲の検証と修正
     */
    validateAndFixDateRangeByCampaign(dateRange) {
      if (!dateRange || !dateRange[0] || !dateRange[1] || !this.campaign) {
        return dateRange
      }

      const campaignStart = new Date(this.getStartDate())
      const campaignEnd = new Date(this.getEndDate())

      let startDate = typeof dateRange[0] === 'string' ? new Date(dateRange[0]) : dateRange[0]
      let endDate = typeof dateRange[1] === 'string' ? new Date(dateRange[1]) : dateRange[1]

      const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
      const campaignStartDateOnly = new Date(
        campaignStart.getFullYear(),
        campaignStart.getMonth(),
        campaignStart.getDate()
      )

      if (startDateOnly.getTime() === campaignStartDateOnly.getTime()) {
        if (startDate < campaignStart) {
          startDate = new Date(campaignStart)
        }
      }

      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
      const campaignEndDateOnly = new Date(campaignEnd.getFullYear(), campaignEnd.getMonth(), campaignEnd.getDate())

      if (endDateOnly.getTime() === campaignEndDateOnly.getTime()) {
        if (endDate > campaignEnd) {
          endDate = new Date(campaignEnd)
        }
      }

      return [this.formatDatetime(startDate), this.formatDatetime(endDate)]
    },

    /**
     * 変更イベントハンドラー
     */
    handleChange(value) {
      const validatedValue = this.validateAndFixDateRangeByCampaign(value)
      this.internalValue = validatedValue || ['', '']

      if (this.onChange) {
        this.onChange(validatedValue)
      }

      this.$emit('input', validatedValue)
      this.$emit('change', validatedValue)
    }
  }
}
</script>

<style lang="scss" scoped>
.app-date-range-picker {
  max-width: 380px;
}

.el-range-editor.el-input__inner {
  box-sizing: border-box !important;
  display: flex !important;
  gap: 0 !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: auto !important;
}

:deep(.el-input__icon) {
  margin-right: 0 !important;
  margin-left: 4px !important;
}

.el-range-input {
  flex: 0 0 auto !important;
  text-align: center !important;
}

:deep(.el-range-separator) {
  padding: 0 !important;
}

:deep(.mdi-calendar-clock) {
  &::before {
    font-size: 18px !important;
  }
}

:deep(.el-input__icon.el-range__close-icon) {
  display: none !important;
  width: 0 !important;
}
</style>
